import { minutesToDuration } from '../../../../lib/DateTime.js';
import { useEffect, useRef, useState } from 'react';
import SelectionBlock from '../components/SelectionBlock.jsx';
import ErrorMessage from '../../../ErrorMessage';
import { useEventTypesQuery } from '../../../../lib/SharedQueries.js';
import { formatCurrency } from '~/lib/CurrencyHelpers';

export function useQuestionEventTypes(
    bookableResourceId,
    settings,
    isVisible,
    startTime,
    extraTime,
) {
    const { isLoading, data } = useEventTypesQuery(
        bookableResourceId,
        settings?.bookableResources,
        {
            disabled: !isVisible,
            extraTime,
            ignoreEvents: settings?.ignoreEvents,
        },
        startTime,
    );

    let types = data || [];
    if (!types.filter) {
        return [];
    }

    types = types.filter(type => {
        // If we're selecting a bookableResource first and the event types requires 2 people then omit it...
        if (
            type?.settings?.numberBookableResourcesRequired > 1 &&
            bookableResourceId > 0
        ) {
            return false;
        }

        // In the calendar-first flow the start time is picked before the event
        //  type, which series and variable multiday types can't satisfy.
        if (
            settings?.stepsOrder === 'personcalendar' &&
            (type.is_series || type.is_variable_multiday)
        ) {
            return false;
        }

        if (settings?.eventTypes && settings?.eventTypes.length > 0) {
            return settings.eventTypes.indexOf(type.id) > -1;
        } else {
            return true;
        }
    });

    return {
        isLoading,
        types,
    };
}

const SelectEventType = ({
    value,
    onChange,
    onChangeCapacity,
    bookableResourceId,
    startTime,
    settings,
    selectedRightContent,
    extraTime,
}) => {
    const [selectedCategory, setSelectedCategory] = useState(null);

    const { isLoading, types } = useQuestionEventTypes(
        bookableResourceId,
        settings,
        true,
        startTime,
        extraTime,
    );

    function durationText(type) {
        if (type.is_variable_multiday) {
            return `${parseInt(type.duration / 1440)}-${type.settings.max_days} days`;
        } else {
            return minutesToDuration(type.duration);
        }
    }

    const allTypeOptions = types.map(type => ({
        title: type.name,
        image: !settings?.compact && type.image,
        category: type.category?.name,
        subtitle: settings.hideDuration
            ? ''
            : durationText(type)
        ,
        description: !settings?.compact && !settings?.hideDescription && type.description,
        price: settings.hidePrice ? '' : type.price,
        priceSuffix: settings.hidePrice || !type.is_variable_multiday ? '' : (type.settings?.pricing_per === 'night' ? 'per night' : 'per day'),
        discountedPrice: type.discounted_price,
        capacity: type.settings?.max_attendees || 1,
        value: type.id,
    }));

    const allOptionsHaveCategory =
        allTypeOptions.filter(type => !type.category).length === 0;
    const categories = [...new Set(allTypeOptions.map(type => type.category))];
    const hasCategoryStep =
        allOptionsHaveCategory &&
        categories.length >= 2 &&
        allTypeOptions.length > 4;

    let categoryOptions = [];
    categories.forEach(category =>
        categoryOptions.push({ title: category, value: category }),
    );

    let typeOptions = allTypeOptions;
    if (hasCategoryStep && selectedCategory) {
        typeOptions = allTypeOptions.filter(
            type => type.category === selectedCategory,
        );
    }

    function setType(type) {
        if (parseInt(type?.value) !== parseInt(value)) {
            onChange(type?.value);
        }

        onChangeCapacity(type?.capacity);
    }

    useEffect(() => {
        if (!!types && value && hasCategoryStep && !selectedCategory) {
            // Make sure category is selected if we're direct linking to something in a category
            const typeOption = typeOptions.find(to => to.value === value);
            if (typeOption) {
                setSelectedCategory(typeOption?.category);
            }
        }
    }, [value, types]);

    useEffect(() => {
        if (typeOptions.length === 1 && typeOptions[0].value !== value) {
            setType(typeOptions[0]);
        }
    }, [JSON.stringify(typeOptions), value]);

    if (
        (settings?.hideEventType /*||
            (allTypeOptions.length === 1 && typeOptions[0].value === value)*/) &&
        !selectedRightContent
    ) {
        return null;
    }

    return (
        <div className="mt-4">
            {isLoading && <div>Loading...</div>}

            {hasCategoryStep && (
                <>
                    <div className="panel-label">Choose Type</div>
                    <SelectionBlock
                        value={selectedCategory}
                        onChange={value => {
                            if (
                                selectedCategory &&
                                selectedCategory !== value
                            ) {
                                // we're changing categories, reset the value
                                setType(null);
                            }
                            setSelectedCategory(value);
                        }}
                        options={categoryOptions}
                        className="mb-4"
                    />
                </>
            )}

            {(!hasCategoryStep || selectedCategory) && (
                <>
                    <SelectionBlock
                        value={value}
                        hideCategoryTitle={hasCategoryStep}
                        onChange={value =>
                            setType(typeOptions.find(to => to.value === value))
                        }
                        options={typeOptions}
                        selectedRightContent={selectedRightContent || (allTypeOptions.length === 1 ? ' ' : null)}
                    />
                </>
            )}
            {typeOptions.length === 0 && !isLoading && (
                <ErrorMessage>
                    There are no types available that can be booked.
                </ErrorMessage>
            )}
        </div>
    );
};

export default SelectEventType;
